// Global Components
import * as React from 'react';
import {
  Col,
  FormGroup,
  Label,
  Row,
} from 'reactstrap';
import {
  AvInput,
} from 'availity-reactstrap-validation';
import {
  AsyncTypeahead,
} from 'react-bootstrap-typeahead';

// Helpers
import _ from 'lodash';

// Types
type $Option = Record<string, unknown> | string;

type $OptionalProps = {
  disabled?: boolean;
  label?: string;
  labelKey?: string;
  onChange?: (value: null | string) => unknown;
  onOptionSelect?: (option: $Option) => unknown;
  required?: boolean;
  valid?: boolean;
  value?: null | string;
};

type $Props = $OptionalProps & {
  getOptions: (search: string) => Promise<Array<$Option>>;
  name: string;
};

type $State = {
  loading: boolean;
  options: Array<$Option>;
  value: null | string;
  value1: string;
  value2: string;
};

type $Vals = {
  value1: string;
  value2: string;
};

type $DisassembleValue = (value?: null | string) => $Vals;

const disassembleValue: $DisassembleValue = (value) => {
  if (value) {
    const parts = value.split('-');

    const value1: string = _.get(
      parts,
      '[0]',
      '',
    );

    const value2: string = _.get(
      parts,
      '[1]',
      '',
    );

    return {
      value1,
      value2,
    };
  }

  return {
    value1: '',
    value2: '',
  };
};

type $AssembleValue = (arg0: $Vals) => null | string;

const assembleValue: $AssembleValue = ({
  value1,
  value2,
}) => {
  if (value1 !== '' && value2 !== '') {
    return `${value1}-${value2}`;
  }

  return null;
};

class InputNoTypeaheadView extends React.Component<$Props, $State> {
  static defaultProps: $OptionalProps = {
    disabled: undefined,
    label: undefined,
    labelKey: undefined,
    onChange: undefined,
    onOptionSelect: undefined,
    required: undefined,
    valid: undefined,
    value: undefined,
  };

  onChange: (value: null | string) => unknown;

  constructor(props: $Props) {
    super(props);

    const {
      value1,
      value2,
    } = disassembleValue(props.value);

    this.state = {
      loading: false,
      options: [],
      value: props.value || null,
      value1,
      value2,
    };

    this.onChange = props.onChange ? _.debounce(
      props.onChange,
      1000,
    ) : () => {};
  }

  setStateAsync(state: Partial<$State>): Promise<void> {
    return new Promise((resolve) => {
      this.setState(
        (pervState: $State) => ({
          ...pervState,
          ...state,
        }),
        resolve,
      );
    });
  }

  async setValues({
    value1,
    value2,
  }: $Vals): Promise<void> {
    const value = assembleValue({
      value1,
      value2,
    });

    await this.setStateAsync({
      value,
      value1,
      value2,
    });

    this.onChange(value);
  }

  render() {
    const {
      disabled,
      getOptions,
      label,
      labelKey,
      name,
      onOptionSelect,
      required,
      valid,
    } = this.props;
    const {
      loading,
      options,
      value,
      value1,
      value2,
    } = this.state;

    return (
      <FormGroup className={'InputNoTypeahead position-relative'}>
        {label && (
          <Label>
            {label}
          </Label>
        )}
        <AsyncTypeahead
          filterBy={() => true}
          id={name}
          isLoading={loading === true}
          labelKey={labelKey || 'Name'}
          onChange={(values: Array<$Option>) => {
            if (onOptionSelect) {
              onOptionSelect(values[0]);
            }
          }}
          onSearch={() => {}}
          options={options}
          renderInput={({
            inputRef,
            referenceElementRef,
            ...inputProps
          }) => {
            const onChange = async (e, values: $Vals) => {
              if (inputProps.onChange) {
                inputProps.onChange(e);
              }

              await this.setValues(values);

              await this.setStateAsync({
                loading: true,
              });

              const foundOptions = await getOptions(assembleValue(values) || '');

              await this.setStateAsync({
                loading: false,
                options: foundOptions,
              });
            };

            return (
              <Row>
                <Col
                  className={'pr-1'}
                  xs={5}
                >
                  <AvInput
                    // eslint-disable-next-line
                    {...inputProps}
                    autoComplete={'new-password'}
                    disabled={disabled}
                    name={`_1${name}`}
                    onChange={(e) => onChange(
                      e,
                      {
                        value1: e.target.value,
                        value2,
                      },
                    )}
                    required={required}
                    value={value1}
                  />
                </Col>
                <Col
                  className={'pl-1'}
                  xs={7}
                >
                  <AvInput
                    // eslint-disable-next-line
                    {...inputProps}
                    autoComplete={'new-password'}
                    disabled={disabled}
                    name={`_2${name}`}
                    onChange={(e) => onChange(
                      e,
                      {
                        value1,
                        value2: e.target.value,
                      },
                    )}
                    required={required}
                    value={value2}
                  />
                </Col>
                <AvInput
                  name={name}
                  ref={(input) => {
                    inputRef(input);
                    referenceElementRef(input);
                  }}
                  style={{
                    display: 'none',
                  }}
                  value={value}
                />
              </Row>
            );
          }}
          useCache={false}
        />
        {valid && (
          <em
            className={'icon-check text-success position-absolute'}
            style={{
              right: 0,
              top: 3,
            }}
          />
        )}
      </FormGroup>
    );
  }
}

export default InputNoTypeaheadView;
